import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Page } from "@/components/app/page";
import { SettingsNav, useSettingsPages } from "@/features/settings/pages";

export const Route = createFileRoute("/settings")({
  component: SettingsLayout,
});

/**
 * Settings: an overview, and a page for each setting with the settings menu beside it on wide
 * screens. A page someone may not see when they open it leads back to the overview; one that goes
 * out of reach while open (users switched off, until the app reloads) stays.
 */
function SettingsLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const { groups, ready } = useSettingsPages();
  const overview = pathname === "/settings" || pathname === "/settings/";
  // While a link leads out of settings, this layout still shows until the new page has loaded,
  // with the new pathname: that is no settings page to send back from.
  const leaving = !overview && !pathname.startsWith("/settings/");
  const allowed =
    overview || groups.some(({ pages }) => pages.some((page) => page.to === pathname));
  // The page that was allowed when it opened, which stays open.
  const admitted = useRef<string | null>(null);
  useEffect(() => {
    if (!ready || leaving || admitted.current === pathname) return;
    if (allowed) admitted.current = pathname;
    else void navigate({ to: "/settings", replace: true });
  }, [ready, leaving, allowed, pathname, navigate]);
  const open = ready && (allowed || admitted.current === pathname);
  return (
    <Page>
      {overview ? (
        <Outlet />
      ) : (
        <div className="grid gap-8 lg:grid-cols-[220px_1fr]">
          <SettingsNav className="sticky top-6 hidden h-fit lg:flex" />
          <div className="min-w-0">{open ? <Outlet /> : null}</div>
        </div>
      )}
    </Page>
  );
}
